<template>
  <div id="monthly-labor-record" ref="crewLaborRecord">
    <div class="monthly-labor-record-table h-full overflow-hidden">
      <vs-row
        id="monthly-labor-record-header"
        vs-w="12"
        class="pt-4 gap-4"
        ref="crewLaborRecordHeader"
      >
        <div class="flex flex-start items-center w-full">
          <div class="flex flex-grow">
            <!-- 年 -->
            <treeselect
              v-model="selectedYear"
              :options="yearOptions"
              :multiple="false"
              :clearable="false"
              placeholder="年"
              noOptionsText="なし"
              class="mr-4 my-1 w-32"
              @input="refetchData"
              noResultsText="見つかりませんでした"
            ></treeselect>
            <!-- 月 -->
            <treeselect
              v-model="selectedMonth"
              :options="monthOptions"
              :multiple="false"
              :clearable="false"
              placeholder="月"
              noOptionsText="なし"
              class="mr-4 my-1 w-32"
              @input="refetchData"
              noResultsText="見つかりませんでした"
            ></treeselect>
          </div>
          <!-- ダウンロードボタン -->
          <div class="flex flex-start items-center">
            <button
              type="button"
              class="vs-button includeIcon download-btn"
              style=""
              :disabled="!isYearMonthSelected"
              @click.stop.prevent="() => openDownloadModal()"
            >
              <feather-icon
                icon="DownloadIcon"
                svgClasses="h-4 w-4"
                class="mr-2"
              />
              <span class="vs-button-text vs-button--text"> ダウンロード </span>
            </button>
          </div>
        </div>
      </vs-row>

      <!-- 作業グラフなどのテーブル -->
      <div
        id="rebor-record-table"
        class="flex flex-col overflow-x-auto"
        ref="crewReborRecordTable"
      >
        <div id="record-header" class="flex flex-shrink-0 sticky z-40">
          <div class="date flex items-end aisea-text-12-400-gray z-50">
            日付
          </div>
          <div
            class="
              aisea-text-12-400-gray
              flex
              items-end
              vessel-of-embarkation-and-role
            "
          >
            乗船先船舶/
            <br>職名
          </div>
          <div
            class="
              working-graph
              flex flex-col
              justify-end
              items-start
              flex-shrink-0
              gap-2
              aisea-text-12-400-dark-gray
            "
          >
            <div>作業グラフ</div>
            <div class="timeline flex flex-shrink-0">
              <div
                v-for="(n, index) of cellNum"
                :key="`${n}-${index}`"
                class="timeline-cell text-center"
              >
                {{ index }}
              </div>
            </div>
          </div>
          <div
            class="
              aisea-text-12-400-gray
              working-hours-per-day
              flex
              items-end
            "
          >
            1日当たりの労働時間
          </div>
          <div
            class="
              aisea-text-12-400-gray
              overtime-work
              flex
              items-end
            "
          >
            時間外
            <br>労働時間
          </div>
          <div
            class="
              aisea-text-12-400-gray
              contiguous-rest-time
              flex
              items-end
            "
          >
            連続
            <br>休息時間
          </div>
          <div
            class="
              aisea-text-12-400-gray
              working-hours-per-week
              flex
              items-end
              justify-center
            "
          >
            過去4日間以内の労働時間
          </div>
          <div
            class="
              aisea-text-12-400-gray
              working-hours-per-week
              flex
              items-end
              justify-center
            "
          >
            過去5日間以内の労働時間
          </div>
          <div
            class="
              aisea-text-12-400-gray
              working-hours-per-week
              flex
              items-end
              justify-center
            "
          >
            過去6日間以内の労働時間
          </div>
          <div
            class="
              aisea-text-12-400-gray
              working-hours-per-week
              flex
              items-end
              justify-center
            "
          >
            過去7日間以内の労働時間
          </div>
          <div
            class="
              aisea-text-12-400-gray
              working-hours-per-week
              flex
              items-end
              justify-center
            "
          >
            過去28日間の時間外労働
          </div>
        </div>
        <!-- 行のデータ -->
        <div id="record-body" class="flex flex-col flex-shrink-0">
          <crew-labor-record-row
            v-for="(dataRecord, index) in rowDataWithTotal"
            :key="index"
            :dateStr="dataRecord.dateStr"
            :momentObj="dataRecord.momentObj"
            :cellSize="cellSize"
            :cellNum="cellNum"
            :vesselOfEmbarkation="dataRecord.vesselOfEmbarkation"
            :role="dataRecord.role"
            :timesheetList="dataRecord.timesheetList"
            :dailyWorkingTime="dataRecord.dailyWorkingTime"
            :dailyOvertime="dataRecord.dailyOvertime"
            :maxOfDividedRestTime="
              dataRecord.maxOfDividedRestTime
              "
            :fourDaysWorkingTime="dataRecord.fourDaysWorkingTime"
            :fiveDaysWorkingTime="dataRecord.fiveDaysWorkingTime"
            :sixDaysWorkingTime="dataRecord.sixDaysWorkingTime"
            :sevendaysWorkingTime="dataRecord.sevendaysWorkingTime"
            :twentyEightDaysOvertime="dataRecord.twentyEightDaysOvertime"
            :warningHours="dataRecord.warningHours"
            :criticalHours="dataRecord.criticalHours"
            :isTotalRow="dataRecord.isTotalRow"
            :isEmbarkationPeriod="dataRecord.isEmbarkationPeriod"
            :isFourDaysWorkingTimeWarning="dataRecord.isFourDaysWorkingTimeWarning"
            :isFiveDaysWorkingTimeWarning="dataRecord.isFiveDaysWorkingTimeWarning"
            :isSixDaysWorkingTimeWarning="dataRecord.isSixDaysWorkingTimeWarning"
            :isSevenDaysWorkingTimeAlert="dataRecord.isSevenDaysWorkingTimeAlert"
            :isSevenDaysWorkingTimeWarning="dataRecord.isSevenDaysWorkingTimeWarning"
            :isTwentyEightDaysOvertimeAlert="dataRecord.isTwentyEightDaysOvertimeAlert"
            :isHourOfRest24hCritical="dataRecord.isHourOfRest24hCritical"
          />
        </div>
      </div>
    </div>
    <vs-prompt
      class="monthly-labor-record-download-dialog"
      :color="AISEA_COLOR.BLUE"
      @close="closeDownloadDialog"
      :active.sync="showDownloadDialog"
      title="勤怠情報 ダウンロード"
      accept-text="ダウンロード"
      cancel-text="キャンセル"
      :is-valid="true"
      buttons-hidden
    >
      <div class="mb-5">ダウンロードする期間を指定してください。</div>
      <div class="flex mb-5">
        <div class="mr-5">
          <label>開始日</label>
          <date-picker
            :value.sync="downloadStartDate"
            placeholder="2022/01/01"
            dateFormat="Y/m/d"
          ></date-picker>
        </div>
        <div class="items-center" style="margin-top: 28px;">〜</div>
        <div class="ml-5">
          <label>終了日</label>
          <date-picker
            :value.sync="downloadEndDate"
            placeholder="2022/01/30"
            dateFormat="Y/m/d"
          ></date-picker>
        </div>
      </div>
      <span class="validation-label">{{
        getDownloadDialogValidationMessage
      }}</span>
      <div class="flex w-full justify-end items-center">
        <div class="m-3 mb-0">
          <vs-button
            type="border"
            style="width: 6rem; padding: 0.4rem"
            @click="closeDownloadDialog"
            :color="AISEA_COLOR.GRAY"
            >キャンセル</vs-button
          >
        </div>
        <div class="m-3 mb-0">
          <vs-button
            :color="AISEA_COLOR.BLUE"
            :disabled="!isDownloadDialogValid"
            style="width: 8rem; padding: 0.4rem"
            @click="
              handleClickDownload(
                enterpriseVesselId,
                downloadStartDate,
                downloadEndDate
              )
            "
            >ダウンロード</vs-button
          >
        </div>
      </div>
    </vs-prompt>
  </div>
</template>

<script>
import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";
import moment from "moment-timezone";
import _ from "lodash";
import CrewLaborRecordRow from "../components/CrewLaborRecordRow";
import SelectYearField from "../components/SelectYearField";
import SelectMonthField from "../components/SelectMonthField";
import StandardWorkingPeriod from "../../standard-working-period/StandardWorkingPeriod";
import HolidaysTable from "../components/HolidaysTable.vue";
import DatePicker from "../../components/DatePicker.vue";
import { DeviceInfoMixin, ColorMixin } from "@/mixins";
import { createNamespacedHelpers } from "vuex";
const { mapActions, mapState, mapMutations } = createNamespacedHelpers(
  "crewLabor"
);
import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
const MONTHS = [
  "1月",
  "2月",
  "3月",
  "4月",
  "5月",
  "6月",
  "7月",
  "8月",
  "9月",
  "10月",
  "11月",
  "12月",
];
const enterpriseOwnerManningVesselCrewRecordBookApi = AiseaApiFactory.get(
  "enterpriseOwnerManningVesselCrewRecordBook"
);
const enterpriseOwnerManningVesselApi = AiseaApiFactory.get(
  "enterpriseOwnerManningVessel"
);
import FileSaver from "file-saver";
const WEEKDAYS = [
  "日曜日",
  "月曜日",
  "火曜日",
  "水曜日",
  "木曜日",
  "金曜日",
  "土曜日",
];
const WEEKDAYS_SHORT = ["日", "月", "火", "水", "木", "金", "土"];

export default {
  name: "CrewLaborRecord",
  components: {
    CrewLaborRecordRow,
    SelectYearField,
    SelectMonthField,
    StandardWorkingPeriod,
    HolidaysTable,
    DatePicker,
    Treeselect,
  },
  props: {
    enterpriseVesselId: { type: String, require: true },
    active: { type: Boolean, default: false },
    cellSize: { type: Number, default: 30 },
    cellNum: { type: Number, default: 24 },
    activeTab: { type: Number },
  },
  mixins: [DeviceInfoMixin, ColorMixin],
  data() {
    return {
      selectedYearMonth: null,
      selectedYear: null,
      selectedMonth: null,
      yearOptions: this.generateYearOptions(2024),
      monthOptions: MONTHS.map((month, index) => {
        return {
          id: index + 1,
          label: month,
        };
      }),
      recordBooks: [],
      vessels: [],
      showDownloadDialog: false,
      downloadStartDate: null,
      downloadEndDate: null,
      totalRow: null,
    };
  },
  watch: {
    enterpriseVesselId() {
      if (!this.active) return;
      this.setUp();
    },
    async active() {
      if (!this.active) return;
      this.setUp();
      this.$nextTick(() => {
        this.setLeborReocrdHeaderHeight();
      });
    },
  },
  computed: {
    ...mapState(["labors"]),
    startAndEndOfMonthDates() {
      const year = this.selectedYear;
      const month = this.selectedMonth;
      if (!year || !month) return {};
      // 月初の日付
      const startOfMonth = moment({
        year: year,
        month: month - 1,
        day: 1,
      }).startOf("month");
      // 月末の日付
      const endOfMonth = moment({ year: year, month: month - 1, day: 1 }).endOf(
        "month"
      );

      return {
        startOfMonth: startOfMonth,
        endOfMonth: endOfMonth,
      };
    },
    isDownloadDialogValid() {
      return !this.getDownloadDialogValidationMessage;
    },
    getDownloadDialogValidationMessage() {
      if (!this.downloadStartDate || !this.downloadEndDate) {
        return "入力されていない項目があります";
      }
      if (
        moment(this.downloadStartDate).isAfter(moment(this.downloadEndDate))
      ) {
        return "開始日が終了日よりも後になるように指定してください";
      }
      if (
        moment(this.downloadEndDate).diff(
          moment(this.downloadStartDate),
          "days"
        ) >= 31
      ) {
        return "開始日と終了日の幅は31日間を超えないように指定してください";
      }
      return null;
    },
    rowData() {
      return this.getRowData(this.recordBooks, this.vessels);
    },
    rowDataWithTotal() {
      if (this.totalRow != null) {
        return [...this.rowData, this.totalRow];
      } else {
        return this.rowData;
      }
    },
    daysOfselectedMonth() {
      if (!this.selectedMoment) return [];
      return this.getSelectedDays(this.selectedMoment.daysInMonth());
    },
    selectedMoment() {
      if (!(this.selectedYear > 0) || !(this.selectedMonth >= 0)) return null;
      return moment({ years: this.selectedYear, months: this.selectedMonth -1 });
    },
    isYearMonthSelected() {
      return this.selectedYear !== null && this.selectedMonth !== null;
    },
  },
  methods: {
    ...mapActions(["fetchLaborPeriodList"]),
    ...mapMutations(["SET_ENTERPRISE_CREW_ID", "SET_LABORS"]),
    generateYearOptions(startYear) {
        const currentYear = moment().year();
        const yearArray = [];

        for (let year = startYear; year <= currentYear; year++) {
          yearArray.push({
            id: year,
            label: `${year}年`,
          });
        }

        return yearArray;
    },
    async fetchRecordBooks(enterpriseCrewId, payload) {
      return enterpriseOwnerManningVesselCrewRecordBookApi
        .recordBooks(enterpriseCrewId, payload)
        .then(res => {
          if (res.status !== true) throw new Error();
          if (!res.data.inline_response_enterprise_crew_record_books)
            throw new Error();
          this.recordBooks =
            res.data.inline_response_enterprise_crew_record_books;

          this.totalRow = {
            dailyOvertime: res.data.total_daily_overtime,
            dailyWorkingTime: res.data.total_daily_working_time,
            isTotalRow: true,
          };
        })
        .catch(err => {
          console.log(err);
          this.$vs.notify({
            title: "勤怠情報の取得に失敗しました",
            color: "danger",
            iconPack: "feather",
            icon: "icon-x-square",
          });
        })
        .finally(() => {
          this.$vs.loading.close();
        });
    },
    async fetchVessels(enterpriseCrewId, payload) {
      return enterpriseOwnerManningVesselApi
      .vessels(enterpriseCrewId, payload)
        .then(res => {
          if (res.status !== true) throw new Error();
          if (!res.data.enterprise_manning_vessel_crews)
            throw new Error();
          this.vessels =
            res.data.enterprise_manning_vessel_crews;
        })
        .catch(err => {
          console.log(err);
          this.$vs.notify({
            title: "勤怠情報の取得に失敗しました",
            color: "danger",
            iconPack: "feather",
            icon: "icon-x-square",
          });
        })
        .finally(() => {
          this.$vs.loading.close();
        });
    },
    refetchData() {
    this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
    this.setUp();
    },
    async setUp() {
      const { startOfMonth, endOfMonth } = this.startAndEndOfMonthDates;
        this.fetchRecordBooks(this.enterpriseVesselId, {
          params: {
            yyyymm: startOfMonth.format("YYYYMM"),
          },
        }),
        this.fetchVessels(this.enterpriseVesselId, {
          params: {
            started_at: startOfMonth.format("YYYY-MM-DD"),
            ended_at: endOfMonth.format("YYYY-MM-DD"),
          },
        })
      this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
    },
    tearDown() {},
    // テーブルの高さを決めるために、ヘッダ部分の高さを取得する
    setLeborReocrdHeaderHeight() {
      const crewLaborRecordHeader = this.$refs.crewLaborRecordHeader;
      this.$refs.crewLaborRecord.style.setProperty(
        "--monthly-labor-record-header-height",
        `${crewLaborRecordHeader.$el.getBoundingClientRect().height}px`
      );
    },
    getDateStr(momentObj) {
      return momentObj.format("M/D (ddd)");
    },
    // 行を作成するのに使用する
    getSelectedDays(num) {
      return Array.from({ length: num }, (_value, index) => index + 1);
    },
    getRowData(recordBooks, vessels) {
      const momentObj = moment(this.selectedMoment).startOf("month");
      momentObj.locale("ja", {
        weekdays: WEEKDAYS,
        weekdaysShort: WEEKDAYS_SHORT,
      });
      const rowList = this.daysOfselectedMonth.map((day, i) => {
        if (i > 0) momentObj.add(1, "day");
        const dateFormateRecordBook = momentObj.format("YYYYMMDD");
        const recordBook =
          recordBooks.find(rb => rb.date === dateFormateRecordBook) || {};
        
        let vesselAndRole = {
          vesselName: "-",
          role: "-"
        };
        let isEmbarkationPeriod = false;

        if (vessels.length > 0) {
          const vesselWithinPeriod = vessels.find(
            v => momentObj.isBetween(moment(v.daytime_in), moment(v.daytime_out), null, "[]") && // 乗船期間内かどうか
            v.vessel.enterprise_owner_manning_vessels.some(
              eomv => momentObj.isBetween(moment(eomv.start_date), moment(eomv.end_date), null, "[]"))); // 連携対象期間内かどうか
          if (vesselWithinPeriod) {
            isEmbarkationPeriod = true;
            vesselAndRole.vesselName = vesselWithinPeriod.vessel.name
            const role = vesselWithinPeriod.roles.find(
              r => momentObj.isBetween(moment(r.start_on), moment(r.end_on), null, "[]"));
            vesselAndRole.role = role.localized_key === "other" && role.name
              ? role.name
              : this.$t(`Views.Crew.${role.localized_key}`);
          } else {
            isEmbarkationPeriod = false;
          }
        } else {
          isEmbarkationPeriod = false;
        }

        return {
          year: this.selectedYear,
          dateStr: this.getDateStr(momentObj),
          momentObj: moment(momentObj),
          month: this.selectedMonth,
          date: recordBook.date || dateFormateRecordBook,
          // 作業グラフ上の打刻に使用
          timesheetList: [...(recordBook.timesheet_list || [])],
          warningHours: [...(recordBook.warning_hours || [])],
          criticalHours: [...(recordBook.critical_hours || [])],
          dailyWorkingTime: recordBook.daily_working_time, // 1日当たりの労働時間
          dailyOvertime: recordBook.daily_overtime, // 時間外労働
          maxOfDividedRestTime:
            recordBook.max_of_divided_rest_time, // 連続休息時間 分割時の長い方の休息時間(日をまたがない)
          lateNightOvertime: recordBook.late_night_overtime, // 深夜労働
          fourDaysWorkingTime: recordBook["4days_working_time"], // 過去4日間以内の労働時間
          fiveDaysWorkingTime: recordBook["5days_working_time"], // 過去5日間以内の労働時間
          sixDaysWorkingTime: recordBook["6days_working_time"], // 過去6日間以内の労働時間
          sevendaysWorkingTime: recordBook["7days_working_time"], // 過去7日間以内の労働時間
          twentyEightDaysOvertime: recordBook["28days_overtime"], // 過去28日間以内の時間外労働
          vesselOfEmbarkation: vesselAndRole.vesselName,
          role: vesselAndRole.role,
          isEmbarkationPeriod: isEmbarkationPeriod,
          isFourDaysWorkingTimeWarning: recordBook.is_hour_of_work4d_warning,
          isFiveDaysWorkingTimeWarning: recordBook.is_hour_of_work5d_warning,
          isSixDaysWorkingTimeWarning: recordBook.is_hour_of_work6d_warning,
          isSevenDaysWorkingTimeAlert: recordBook.is_hour_of_work1w_critical,
          isSevenDaysWorkingTimeWarning: recordBook.is_hour_of_work1w_warning,
          isTwentyEightDaysOvertimeAlert: recordBook.is_hour_of_overtime_work28d_critical,
          isHourOfRest24hCritical: recordBook.is_hour_of_rest24h_critical,
        };
      });

      return rowList;
    },
    openDownloadModal() {
      this.downloadStartDate = moment({year: this.selectedYear, month:this.selectedMonth - 1}).toDate();
      this.downloadEndDate = moment({year: this.selectedYear, month:this.selectedMonth})
        .add(-1, "day")
        .toDate();
      this.showDownloadDialog = true;
    },
    closeDownloadDialog() {
      this.showDownloadDialog = false;
    },
    async handleClickDownload(enterpriseVesselId, startAt, endAt) {
      this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
      const startAtString = moment(startAt).format("YYYY-MM-DD");
      const endAtString = moment(endAt).format("YYYY-MM-DD");
      try {
        const res = await enterpriseOwnerManningVesselCrewRecordBookApi.export(
          enterpriseVesselId,
          {
            params: {
              start_at: startAtString,
              end_at: endAtString,
            },
          }
        );
        if (res.status !== true) throw new Error();
        const fileName = `労務管理記録簿_${startAtString}_${endAtString}.xlsx`;
        FileSaver.saveAs(res.data, fileName);
        this.$vs.loading.close();
      } catch (e) {
        this.$vs.loading.close();
        this.$vs.notify({
          title: "ダウンロードに失敗しました",
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square",
        });
      }
    },
    async searchCrewLabors() {
      await this.fetchLaborPeriodList();
    },
  },
  beforeCreate() {},
  async created() {
    this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
      this.selectedMonth = this.monthOptions.find(
        month => month.id === moment().month() + 1
      ).id;
      this.selectedYear = this.yearOptions.find(
        year => year.id === moment().year()
      ).id;
    this.setUp();
  },
  beforeMount() {},
  async mounted() {
    this.$nextTick(() => {
      this.setLeborReocrdHeaderHeight();
    });
  },
  beforeUpdate() {},
  updated() {},
  beforeDestroy() {},
  destroyed() {
    this.tearDown();
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/aisea/_variables.scss";

#monthly-labor-record {
  --vs-primary: 3, 155, 229;
  height: 100%;
  .vx-card {
    padding: 20px;
  }
  #monthly-labor-record-header {
    .download-btn {
      border: 1px solid rgb(var(--vs-primary));
      background: transparent;
      color: rgb(var(--vs-primary));
      height: 2.7rem;
    }
  }
  #rebor-record-table {
    #record-header {
      width: fit-content;
      top: 0;
      left: 0;
      height: 50px;
      border-bottom: 1px solid $aisea-light-gray;
      background-color: $aisea-pure-white;
      > div {
        position: sticky;
        top: 0;
        flex-shrink: 0;
        height: 49px;
        border-radius: 0px;
        padding: 0px, 20px, 0px, 10px;
        &:first-child {
          position: sticky;
          top: 0;
          left: 0;
        }
      }
      > div:not(.working-graph) {
        min-width: 80px;
        width: 80px;
        padding: 2px;
        padding-bottom: 10px;

        &.date {
          min-width: 85px;
          padding: 9px;
          background-color: $aisea-pure-white;
        }
        &.vessel-of-embarkation-and-role {
          width: 120px;
          padding: 9px;
          text-align: left;
        }
        &.working-hours-per-day {
          padding: 9px;
        }
        &.overtime-work {
          padding: 9px;
          padding-bottom: 10px;
        }
        &.contiguous-rest-time {
          padding: 9px;
          text-align: left;
        }
      }
      .timeline-cell {
        height: 20px;
        min-width: 30px;
        border-left: solid 1px #e0e0e0;
        &:last-child {
          border-right: solid 1px #e0e0e0;
        }
      }
    }
    #record-body {
      width: fit-content;
    }
  }
}

.monthly-labor-record-download-dialog {
  label {
    &:after {
      content: " *";
      font-size: 0.8rem;
      color: rgba(var(--vs-danger), 1);
    }
  }
  .validation-label {
    color: red;
    margin-top: 4px;
    margin-bottom: 4px;
  }
}
</style>
